import React, { useCallback, useEffect, useMemo, useState } from 'react';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function formatDateTime(value) {
  if (!value) return 'Not recorded yet';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

function StatusBadge({ status }) {
  const colors = {
    succeeded: 'bg-green-100 text-green-800',
    partial: 'bg-amber-100 text-amber-800',
    failed: 'bg-red-100 text-red-800',
    running: 'bg-blue-100 text-blue-800',
  };
  const labels = {
    succeeded: 'Successful',
    partial: 'Needs attention',
    failed: 'Failed',
    running: 'Running',
  };
  return (
    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {labels[status] || status}
    </span>
  );
}

export default function BackgroundTaskMonitor() {
  const [data, setData] = useState({ summary: {}, runs: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [retrying, setRetrying] = useState({});
  const [openRuns, setOpenRuns] = useState({});

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/firm/background-tasks/?limit=60`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'Could not load background activity.');
      setData({ summary: payload.summary || {}, runs: payload.runs || [] });
    } catch (requestError) {
      setError(requestError.message || 'Could not load background activity.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load({ quiet: true }), 30000);
    return () => window.clearInterval(timer);
  }, [load]);

  const visibleRuns = useMemo(() => {
    if (showAll) return data.runs;
    return data.runs.filter((run) => ['failed', 'partial', 'running'].includes(run.status));
  }, [data.runs, showAll]);

  async function retry(run) {
    const targetId = run.id;
    if (retrying[targetId]) return;
    setRetrying((current) => ({ ...current, [targetId]: true }));
    setError('');
    try {
      const response = await fetch(`${API_BASE}/firm/background-tasks/${targetId}/retry/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({}),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'The retry could not be queued.');
      await load({ quiet: true });
    } catch (requestError) {
      setError(requestError.message || 'The retry could not be queued.');
    } finally {
      setRetrying((current) => ({ ...current, [targetId]: false }));
    }
  }

  const summary = data.summary || {};
  const cards = [
    ['Last docket fetch', formatDateTime(summary.last_successful_docket_fetch)],
    ['Emails/reminders sent (24h)', summary.emails_sent_24h ?? 0],
    ['Failed emails (24h)', summary.emails_failed_24h ?? 0],
    ['Automatic uploads (24h)', summary.automatic_document_uploads_24h ?? 0],
    ['Celery task issues (24h)', summary.task_failures_24h ?? 0],
  ];
  const recentFailed = data.runs.filter((run) => run.status === 'failed').length;
  const recentAttention = data.runs.filter((run) => run.status === 'partial').length;
  const running = data.runs.filter((run) => run.status === 'running').length;
  const failedEmails = Number(summary.emails_failed_24h || 0);
  const hasIssues = recentFailed > 0 || recentAttention > 0 || failedEmails > 0 || Boolean(error);

  return (
    <section className="overflow-hidden rounded-lg bg-white shadow">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls="automation-health-details"
        onClick={() => setIsOpen((value) => !value)}
        className="flex w-full flex-col gap-4 px-6 py-5 text-left hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="min-w-0">
          <h2 className="text-xl font-semibold text-gray-900">Automation Health</h2>
          <p className="mt-1 text-sm text-gray-600">
            Docket imports, reminders, document uploads, and other scheduled work.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {loading ? (
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
                Checking status…
              </span>
            ) : (
              <>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${recentFailed ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                  {recentFailed} failed
                </span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${recentAttention ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>
                  {recentAttention} need attention
                </span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${running ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>
                  {running} running
                </span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${failedEmails ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}`}>
                  {failedEmails} failed emails (24h)
                </span>
                {!hasIssues && !running && (
                  <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-800">
                    No current issues
                  </span>
                )}
                {error && (
                  <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800">
                    Status unavailable
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 text-sm font-semibold text-indigo-700">
          <span>{isOpen ? 'Hide details' : 'View details'}</span>
          <span className={`text-xl leading-none transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true">⌄</span>
        </div>
      </button>

      {isOpen && (
        <div id="automation-health-details" className="border-t border-gray-200 px-6 pb-6">
          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={() => load()}
              disabled={loading}
              className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {cards.map(([label, value]) => (
          <div key={label} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
            <div className="mt-2 text-sm font-semibold text-gray-900">{value}</div>
          </div>
        ))}
      </div>

      {error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">
          {showAll ? 'Recent task runs' : 'Recent issues and running tasks'}
        </h3>
        <button
          type="button"
          onClick={() => setShowAll((value) => !value)}
          className="text-sm font-semibold text-indigo-600 hover:text-indigo-800"
        >
          {showAll ? 'Show issues only' : 'Show all recent runs'}
        </button>
      </div>

      <div className="mt-3 space-y-3">
        {!loading && visibleRuns.length === 0 && (
          <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-4 text-sm text-green-800">
            No recent background-task problems are recorded.
          </div>
        )}
        {visibleRuns.map((run) => {
          const open = Boolean(openRuns[run.id]);
          const failedItems = (run.items || []).filter((item) => item.status === 'failed');
          return (
            <div key={run.id} className="overflow-hidden rounded-lg border border-gray-200">
              <button
                type="button"
                onClick={() => setOpenRuns((current) => ({ ...current, [run.id]: !open }))}
                className="flex w-full items-center justify-between gap-4 bg-white px-4 py-3 text-left hover:bg-gray-50"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-gray-900">{run.display_name}</div>
                  <div className="mt-1 text-xs text-gray-500">
                    {formatDateTime(run.started_at)}
                    {failedItems.length ? ` · ${failedItems.length} failed item${failedItems.length === 1 ? '' : 's'}` : ''}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <StatusBadge status={run.status} />
                  <span className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}>⌄</span>
                </div>
              </button>

              {open && (
                <div className="border-t border-gray-200 bg-gray-50 px-4 py-4">
                  <p className="text-sm text-gray-700">{run.summary || 'No summary was recorded.'}</p>
                  {run.error_message && (
                    <pre className="mt-3 max-h-56 overflow-auto whitespace-pre-wrap rounded-md bg-red-950 p-3 text-xs text-red-50">
                      {run.error_message}
                    </pre>
                  )}
                  {(run.items || []).length > 0 && (
                    <div className="mt-4 space-y-2">
                      {(run.items || []).map((item) => (
                        <div key={item.id} className="rounded-md border border-gray-200 bg-white p-3 text-sm">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="font-semibold text-gray-900">{item.label || item.item_type}</div>
                            <StatusBadge status={item.status} />
                          </div>
                          {item.case && (
                            <a
                              href={`/case/${item.case.case_id}`}
                              className="mt-1 inline-block text-sm font-medium text-indigo-600 hover:underline"
                            >
                              {item.case.name} · {item.case.case_number || item.case.case_id}
                            </a>
                          )}
                          {item.summary && <p className="mt-1 text-gray-600">{item.summary}</p>}
                          {item.error_message && (
                            <pre className="mt-2 overflow-auto whitespace-pre-wrap rounded bg-red-50 p-2 text-xs text-red-800">
                              {item.error_message}
                            </pre>
                          )}
                          {item.retryable && item.status === 'failed' && (
                            <button
                              type="button"
                              onClick={() => retry(item)}
                              disabled={retrying[item.id] || Boolean(item.retry_requested_at)}
                              className="mt-3 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                            >
                              {item.retry_requested_at ? 'Retry queued' : retrying[item.id] ? 'Queueing…' : 'Retry failed item'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {run.retryable && ['failed', 'partial'].includes(run.status) && (
                    <button
                      type="button"
                      onClick={() => retry(run)}
                      disabled={retrying[run.id] || Boolean(run.retry_requested_at)}
                      className="mt-4 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {run.retry_requested_at ? 'Retry queued' : retrying[run.id] ? 'Queueing…' : 'Retry task'}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
        </div>
      )}
    </section>
  );
}
